import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { typesService } from '../../services/typesService';
import { CopyUrlButton } from './CopyUrlButton';
import { useI18n } from '../../contexts/I18nContext';
import './FirmwareDetailModal.css';

interface FirmwareData {
  id: number;
  model: string;
  type: string;
  version: string;
  file_url: string;
  from_version: string | null;
  file_size: number | null;
  checksum: string | null;
  changelog: string | null;
  is_active: boolean;
  client_id: string | null;
  created_at: string;
  updated_at: string;
}

interface FirmwareDetailModalProps {
  isOpen: boolean;
  firmware: FirmwareData | null;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
}

interface ClientOption {
  id: string;
  name: string;
}

interface ModelOption {
  code: string;
  name: string;
  hardwareType: string;
}

const COMPONENT_TYPES = ['MAIN', 'WWWDATA', 'GSM'];

export const FirmwareDetailModal: React.FC<FirmwareDetailModalProps> = ({
  isOpen,
  firmware,
  onClose,
  onSave,
  onDelete
}) => {
  const { formatDateTime, t } = useI18n();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [modelOptions, setModelOptions] = useState<ModelOption[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState({
    model: '',
    type: '',
    version: '',
    file_url: '',
    from_version: '',
    file_size: '',
    checksum: '',
    changelog: '',
    is_active: true,
    client_id: ''
  });

  const axiosInstance = useMemo(() => apiClient.getAxiosInstance(), []);

  // Load firmware data into form when opened
  useEffect(() => {
    if (firmware && isOpen) {
      setFormData({
        model: firmware.model || '',
        type: firmware.type || '',
        version: firmware.version || '',
        file_url: firmware.file_url || '',
        from_version: firmware.from_version || '',
        file_size: firmware.file_size?.toString() || '',
        checksum: firmware.checksum || '',
        changelog: firmware.changelog || '',
        is_active: firmware.is_active,
        client_id: firmware.client_id || ''
      });
      setIsEditing(false);
      setError(null);
      setShowDeleteConfirm(false);
    }
  }, [firmware, isOpen]);

  // Fetch clients and model references
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen) return;

      try {
        const clientList = await clientService.fetchActiveClients();
        setClients(clientList);
      } catch (err) {
        console.warn('Could not fetch clients');
      }

      try {
        const hardwareTypes = await typesService.getHardwareTypes(false);
        const allModels: ModelOption[] = [];
        for (const hwType of hardwareTypes) {
          const modelRefs = await typesService.getModelReferences(hwType.id, false);
          for (const ref of modelRefs) {
            allModels.push({
              code: ref.code,
              name: ref.name || ref.code,
              hardwareType: hwType.code
            });
          }
        }
        setModelOptions(allModels);
      } catch (err) {
        console.warn('Could not fetch model references');
      }
    };

    fetchData();
  }, [isOpen]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!firmware) return;

    setIsLoading(true);
    setError(null);

    try {
      const payload: any = {
        model: formData.model,
        type: formData.type,
        version: formData.version,
        file_url: formData.file_url,
        is_active: formData.is_active
      };

      if (formData.from_version) payload.from_version = formData.from_version;
      if (formData.file_size) payload.file_size = parseInt(formData.file_size);
      if (formData.checksum) payload.checksum = formData.checksum;
      if (formData.changelog) payload.changelog = formData.changelog;
      if (formData.client_id) payload.client_id = formData.client_id;

      await axiosInstance.patch(`/v2/firmware/${firmware.id}`, payload);
      setIsEditing(false);
      onSave();
    } catch (err: any) {
      console.error('Failed to update firmware:', err);
      setError(err.response?.data?.detail || 'Failed to update firmware');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!firmware) return;

    setIsLoading(true);
    setError(null);

    try {
      await axiosInstance.delete(`/v2/firmware/${firmware.id}`);
      onDelete();
      onClose();
    } catch (err: any) {
      console.error('Failed to delete firmware:', err);
      setError(err.response?.data?.detail || 'Failed to delete firmware');
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatDate = (dateString: string) => formatDateTime(dateString);

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getClientName = (clientId: string | null) => {
    if (!clientId) return 'All Clients (Global)';
    const client = clients.find(c => c.id === clientId);
    return client?.name || clientId;
  };

  if (!isOpen || !firmware) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="firmware-detail-modal" data-testid="modal-firmware-detail" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-title-section">
            <h2>{t('otaUpdates.firmwareDetails')}</h2>
            <span className={`status-indicator ${firmware.is_active ? 'active' : 'inactive'}`}>
              {firmware.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Delete Confirmation Overlay */}
        {showDeleteConfirm && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm-dialog">
              <span className="delete-confirm-icon">!</span>
              <p>Are you sure you want to delete this firmware version?</p>
              <div className="delete-confirm-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={isLoading}
                >
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="modal-content">
          {/* Section 1: Identity */}
          <div className="detail-section">
            <h3 className="section-title">{t('otaUpdates.firmwareIdentity')}</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>{t('otaUpdates.model')}</label>
                {isEditing ? (
                  <select
                    value={formData.model}
                    onChange={(e) => handleChange('model', e.target.value)}
                    className="field-select"
                  >
                    {modelOptions.map(model => (
                      <option key={model.code} value={model.code}>
                        {model.code}{model.name !== model.code ? ` - ${model.name}` : ''} ({model.hardwareType})
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="detail-value">{firmware.model}</span>
                )}
              </div>

              <div className="detail-item">
                <label>{t('otaUpdates.componentType')}</label>
                {isEditing ? (
                  <select
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    className="field-select"
                  >
                    {COMPONENT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                ) : (
                  <span className="detail-value type-badge">{firmware.type}</span>
                )}
              </div>

              <div className="detail-item">
                <label>{t('otaUpdates.version')}</label>
                {isEditing ? (
                  <input
                    type="text"
                    data-testid="field-firmware-version-edit"
                    value={formData.version}
                    onChange={(e) => handleChange('version', e.target.value)}
                    className="field-input"
                  />
                ) : (
                  <span className="detail-value version-value">{firmware.version}</span>
                )}
              </div>

              <div className="detail-item">
                <label>{t('otaUpdates.fromVersion')}</label>
                {isEditing ? (
                  <input
                    type="text"
                    data-testid="field-firmware-from-version-edit"
                    value={formData.from_version}
                    onChange={(e) => handleChange('from_version', e.target.value)}
                    className="field-input"
                    placeholder={t('otaUpdates.anyVersion')}
                  />
                ) : (
                  <span className="detail-value">{firmware.from_version || 'Any version'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Section 2: File Information */}
          <div className="detail-section">
            <h3 className="section-title">{t('otaUpdates.fileInformation')}</h3>
            <div className="detail-grid">
              <div className="detail-item detail-item-full">
                <label>{t('otaUpdates.fileUrl')}</label>
                {isEditing ? (
                  <div className="url-input-group">
                    <input
                      type="text"
                      data-testid="field-firmware-file-url-edit"
                      value={formData.file_url}
                      onChange={(e) => handleChange('file_url', e.target.value)}
                      className="field-input"
                    />
                    {formData.file_url && <CopyUrlButton url={formData.file_url} />}
                  </div>
                ) : (
                  <div className="url-display-group">
                    <span className="detail-value url-value">{firmware.file_url}</span>
                    {firmware.file_url && <CopyUrlButton url={firmware.file_url} />}
                  </div>
                )}
              </div>

              <div className="detail-item">
                <label>{t('otaUpdates.fileSize')}</label>
                {isEditing ? (
                  <input
                    type="text"
                    data-testid="field-firmware-file-size-edit"
                    value={formData.file_size}
                    onChange={(e) => handleChange('file_size', e.target.value)}
                    className="field-input"
                    placeholder={t('otaUpdates.fileSizePlaceholder')}
                  />
                ) : (
                  <span className="detail-value">{formatFileSize(firmware.file_size)}</span>
                )}
              </div>

              <div className="detail-item">
                <label>Checksum (SHA256)</label>
                {isEditing ? (
                  <input
                    type="text"
                    data-testid="field-firmware-checksum-edit"
                    value={formData.checksum}
                    onChange={(e) => handleChange('checksum', e.target.value)}
                    className="field-input"
                  />
                ) : (
                  <span className="detail-value checksum-value">{firmware.checksum || '-'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Section 3: Deployment */}
          <div className="detail-section">
            <h3 className="section-title">{t('otaUpdates.deployment')}</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>{t('clients.client')}</label>
                {isEditing ? (
                  <select
                    value={formData.client_id}
                    onChange={(e) => handleChange('client_id', e.target.value)}
                    className="field-select"
                  >
                    <option value="">All Clients (Global)</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="detail-value">{getClientName(firmware.client_id)}</span>
                )}
              </div>

              <div className="detail-item">
                <label>{t('otaUpdates.status')}</label>
                {isEditing ? (
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      data-testid="field-firmware-is-active"
                      checked={formData.is_active}
                      onChange={(e) => handleChange('is_active', e.target.checked)}
                    />
                    <span>Active (available for OTA updates)</span>
                  </label>
                ) : (
                  <span className={`detail-value status-value ${firmware.is_active ? 'active' : 'inactive'}`}>
                    {firmware.is_active ? 'Active' : 'Inactive'}
                  </span>
                )}
              </div>

              <div className="detail-item detail-item-full">
                <label>Changelog / Release Notes</label>
                {isEditing ? (
                  <textarea
                    value={formData.changelog}
                    onChange={(e) => handleChange('changelog', e.target.value)}
                    className="field-textarea"
                    rows={3}
                  />
                ) : (
                  <span className="detail-value changelog-value">
                    {firmware.changelog || 'No changelog available'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Section 4: Metadata */}
          <div className="detail-section">
            <h3 className="section-title">{t('otaUpdates.metadata')}</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>{t('clients.created')}</label>
                <span className="detail-value">{formatDate(firmware.created_at)}</span>
              </div>
              <div className="detail-item">
                <label>{t('otaUpdates.lastUpdated')}</label>
                <span className="detail-value">{formatDate(firmware.updated_at)}</span>
              </div>
              <div className="detail-item">
                <label>ID</label>
                <span className="detail-value">{firmware.id}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-actions">
          <div className="action-group-left">
            {!isEditing && (
              <button
                className="btn btn-danger-outline"
                data-testid="btn-firmware-delete"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isLoading || showDeleteConfirm}
              >
                Delete
              </button>
            )}
          </div>
          <div className="action-group-right">
            {isEditing ? (
              <>
                <button
                  className="btn btn-secondary"
                  data-testid="btn-firmware-cancel"
                  onClick={() => {
                    setIsEditing(false);
                    // Reset form to original values
                    if (firmware) {
                      setFormData({
                        model: firmware.model || '',
                        type: firmware.type || '',
                        version: firmware.version || '',
                        file_url: firmware.file_url || '',
                        from_version: firmware.from_version || '',
                        file_size: firmware.file_size?.toString() || '',
                        checksum: firmware.checksum || '',
                        changelog: firmware.changelog || '',
                        is_active: firmware.is_active,
                        client_id: firmware.client_id || ''
                      });
                    }
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  data-testid="btn-firmware-save"
                  onClick={handleSave}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-secondary" data-testid="btn-firmware-close" onClick={onClose}>
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  data-testid="btn-firmware-edit"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
